import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/crmApi";
import { PageHeader, Loader, Empty } from "@/crm/ui";
import { useAuth } from "@/context/CrmAuth";
import { toast } from "sonner";
import { Ban, RotateCcw, ShieldOff, X } from "lucide-react";
import Modal from "@/crm/components/Modal";

const CHANNEL_LABELS = { PHONE: "Téléphone", EMAIL: "Email", SMS: "SMS", ALL: "Tous" };

export default function Exclusions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("true");
  const [reactivating, setReactivating] = useState(null);

  const isManager = user?.role === "admin" || user?.role === "manager";

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (typeFilter) params.append("type", typeFilter);
    if (activeFilter) params.append("active", activeFilter);
    try {
      const { data } = await api.get(`/exclusions?${params}`);
      setItems(data);
    } catch { setItems([]); }
  }, [search, typeFilter, activeFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  if (items === null) return <Loader />;

  return (
    <div>
      <PageHeader title="Oppositions — Ne plus contacter" subtitle="Entreprises et contacts exclus de toute prospection" />

      <div className="flex flex-wrap gap-3 mb-4">
        <input data-testid="excl-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, SIRET, contact)…" className="flex-1 min-w-52 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        <select data-testid="excl-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
          <option value="">Tous types</option>
          <option value="COMPANY">Entreprise</option>
          <option value="CONTACT">Contact</option>
        </select>
        <select data-testid="excl-active-filter" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
          <option value="true">Actives</option>
          <option value="false">Réactivées</option>
          <option value="">Toutes</option>
        </select>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200"><Empty label="Aucune opposition" icon={ShieldOff} /></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="exclusions-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200 bg-slate-50">
                  {["Entreprise", "SIRET", "Contact", "Type", "Motif", "Canaux", "Date", "Créé par", "Statut", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid="exclusion-row">
                    <td className="px-4 py-3">
                      <button onClick={() => navigate(`/entreprises/${e.company_id}`)} className="text-indigo-600 hover:underline font-medium">{e.company_name || "—"}</button>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{e.company_siret || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{e.contact_name || "—"}</td>
                    <td className="px-4 py-3"><span className="text-xs bg-slate-100 rounded px-2 py-0.5">{e.type === "COMPANY" ? "Entreprise" : "Contact"}</span></td>
                    <td className="px-4 py-3 text-slate-600">{e.reason}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{(e.blocked_channels || []).map((c) => CHANNEL_LABELS[c] || c).join(", ")}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(e.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{e.created_by_name || "—"}</td>
                    <td className="px-4 py-3">
                      {e.active
                        ? <span className="inline-flex items-center gap-1 text-xs bg-rose-100 text-rose-700 rounded px-2 py-0.5"><Ban size={11} /> Active</span>
                        : <span className="text-xs bg-slate-100 text-slate-500 rounded px-2 py-0.5">Réactivée</span>}
                    </td>
                    <td className="px-4 py-3">
                      {e.active && isManager && (
                        <button data-testid="reactivate-btn" onClick={() => setReactivating(e)} className="flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600"><RotateCcw size={13} /> Réactiver</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reactivating && <ReactivateModal exclusion={reactivating} onClose={() => setReactivating(null)} onDone={() => { setReactivating(null); load(); }} />}
    </div>
  );
}

function ReactivateModal({ exclusion, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const submit = async () => {
    if (!reason.trim()) return toast.error("Motif requis");
    try {
      await api.post(`/exclusions/${exclusion.id}/reactivate`, { reason });
      toast.success("Opposition réactivée");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Modal onClose={onClose} testid="reactivate-modal" label="Réactiver l'entreprise">
      <div className="flex items-center justify-between gap-3 mb-4"><h3 className="font-heading font-bold text-lg">Réactiver l'entreprise</h3><button onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-900"><X size={18} /></button></div>
      <p className="text-sm text-slate-500 mb-3">Vous levez l'opposition sur <strong>{exclusion.company_name}</strong>. L'historique est conservé.</p>
      <textarea data-testid="reactivate-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Motif de réactivation *" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm mb-4" />
      <button data-testid="reactivate-submit" onClick={submit} className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700">Réactiver</button>
    </Modal>
  );
}
